import { currentState, navigate, onNavigate, paramsMap, scope, stateMap } from '@otto-ec/global-resources/navigation';
import type { SelectionPage, SelectionSource, SelectionState } from '@tilelist/application/ports/SelectionEvents.ts';
import type { SelectionDomAdapter } from './SelectionDependencies.ts';
import { pageFromUrl } from './SelectionState.ts';

export type ForwardSelectionSource = Exclude<SelectionSource, 'historic'>;
export type SelectionNavigationInfo = {
    owner: 'dundee-selection';
    source: ForwardSelectionSource;
    initialize?: boolean;
};

type NavigationEvent = Parameters<Parameters<typeof onNavigate.subscribe>[0]>[0];

export type SelectionNavigation = {
    currentCheckpoint(): unknown;
    initialSelection(dom: SelectionDomAdapter): SelectionState;
    navigate(
        selection: SelectionState,
        source: ForwardSelectionSource,
        history: 'push' | 'replace',
        keepHeurekaFragment: boolean,
        initialize: boolean,
    ): Promise<void>;
    subscribe(handler: (event: NavigationEvent, checkpoint: unknown) => void): () => void;
};

const HEUREKA_FILTERSHEET_FRAGMENT = '#oc-sheet-v1=heureka-filtersheet';

export function relativeLocation(url: URL): string {
    return url.pathname + url.search;
}

export function navigationInfo(value: unknown): SelectionNavigationInfo | undefined {
    if (typeof value !== 'object' || value === null) return undefined;
    if (!('owner' in value) || value.owner !== 'dundee-selection') return undefined;
    if (!('source' in value) || (value.source !== 'page' && value.source !== 'full')) return undefined;
    return {
        owner: 'dundee-selection',
        source: value.source,
        ...('initialize' in value && value.initialize === true ? { initialize: true } : {}),
    };
}

export function isCheckpoint(value: unknown): value is SelectionState {
    if (typeof value !== 'object' || value === null) return false;
    return (
        'rule' in value &&
        typeof value.rule === 'string' &&
        value.rule.length > 0 &&
        'page' in value &&
        isSelectionPage(value.page) &&
        'url' in value &&
        typeof value.url === 'string' &&
        value.url.length > 0 &&
        'entryRule' in value &&
        typeof value.entryRule === 'string' &&
        value.entryRule.length > 0
    );
}

export async function navigateSelection(
    selection: SelectionState,
    source: ForwardSelectionSource,
    history: 'push' | 'replace',
    keepHeurekaFragment: boolean,
    initialize: boolean,
): Promise<void> {
    const url = keepHeurekaFragment ? appendHeurekaFragment(selection.url) : selection.url;
    await navigate(url, {
        history,
        state: { ...selection, url },
        info: { owner: 'dundee-selection', source, ...(initialize ? { initialize: true } : {}) },
    });
}

export function createTopicPageSelectionNavigation(): SelectionNavigation {
    const navigationScope = scope('dundee-selection', {
        search: paramsMap<{ filtered?: string }>(['filtered']),
        state: stateMap<SelectionState>(),
    });

    return {
        currentCheckpoint: () => navigationScope.currentState.get().toObject(),
        initialSelection(dom) {
            const bootstrapRule = dom.bootstrapRule();
            const filteredRule = navigationScope.search().get('filtered') || undefined;
            const url = dom.currentUrl();
            const checkpoint = navigationScope.currentState.get().toObject();
            const entryRule = (isCheckpoint(checkpoint) ? checkpoint.entryRule : undefined) || bootstrapRule;
            return {
                rule: filteredRule || bootstrapRule,
                page: pageFromUrl(url),
                url,
                entryRule,
            };
        },
        async navigate(selection, source, history, keepHeurekaFragment, initialize) {
            if (!initialize) {
                navigationScope.search(selection.rule === selection.entryRule ? {} : { filtered: selection.rule });
            }
            const url = navigationScope.search().toUrl(window.location);
            if (keepHeurekaFragment && !url.hash && window.location.hash === HEUREKA_FILTERSHEET_FRAGMENT) {
                url.hash = HEUREKA_FILTERSHEET_FRAGMENT;
            }
            const checkpoint = { ...selection, url: url.pathname + url.search + url.hash };
            await navigationScope
                .navigate(url, {
                    history,
                    state: checkpoint,
                    info: { owner: 'dundee-selection', source, ...(initialize ? { initialize: true } : {}) },
                })
                .then(() => {
                    /*                                                                             */
                    navigate(url, {
                        history: 'replace',
                        state: { ...currentState.get(), entryRule: checkpoint.entryRule },
                    });
                });
        },
        subscribe(handler) {
            return navigationScope.onNavigate.subscribe((event) => handler(event, event.to.state.toObject()));
        },
    };
}

export const defaultSelectionNavigation: SelectionNavigation = {
    currentCheckpoint: () => currentState.get(),
    initialSelection(dom) {
        const bootstrapRule = dom.bootstrapRule();
        const url = dom.currentUrl();
        const checkpoint = currentState.get();
        return {
            rule: bootstrapRule,
            page: pageFromUrl(url),
            url,
            entryRule: (isCheckpoint(checkpoint) ? checkpoint.entryRule : undefined) || bootstrapRule,
        };
    },
    navigate: navigateSelection,
    subscribe(handler) {
        return onNavigate.subscribe((event) => handler(event, event.destination.state));
    },
};

function isSelectionPage(value: unknown): value is SelectionPage {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    return (
        Object.keys(value).every((key) => ['sortOrder', 'layout', 'offset', 'comparisonId'].includes(key)) &&
        (!('sortOrder' in value) || typeof value.sortOrder === 'string') &&
        (!('layout' in value) || typeof value.layout === 'string') &&
        (!('offset' in value) || typeof value.offset === 'string') &&
        (!('comparisonId' in value) || typeof value.comparisonId === 'string')
    );
}

function appendHeurekaFragment(url: string): string {
    if (url.includes('#') || window.location.hash !== HEUREKA_FILTERSHEET_FRAGMENT) return url;
    return `${url}${HEUREKA_FILTERSHEET_FRAGMENT}`;
}
